import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { Loader } from '../../components';
import { useContracts } from '../../hooks/useContracts';
import { ContractApiResponse } from '../../services/contract-services';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { NavigationConfig } from '../../utils/navigation-constants';

const MAKEPAYMENT_CONTRACTID_STORAGE_KEY = 'makepayment_pending_contractid';

// Helper functions to use localStorage instead of sessionStorage
const storePendingContractId = (contractId: string) => {
    localStorage.setItem(MAKEPAYMENT_CONTRACTID_STORAGE_KEY, contractId);
};

const getPendingContractId = () => {
    return localStorage.getItem(MAKEPAYMENT_CONTRACTID_STORAGE_KEY);
};

const clearPendingContractId = () => {
    localStorage.removeItem(MAKEPAYMENT_CONTRACTID_STORAGE_KEY);
};

// Case-insensitive parameter lookup
const getSearchParam = (searchParams: URLSearchParams, paramName: string): string | null => {
    for (const [key, value] of searchParams.entries()) {
        if (key.toLowerCase() === paramName.toLowerCase()) {
            return value;
        }
    }
    return null;
};

const MakePaymentLink: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
    const { fetchContracts } = useContracts();

    useEffect(() => {
        // Check URL params first, then fall back to localStorage (for post-login case)
        console.log('[MakePaymentLink] URL searchParams:', Array.from(searchParams.entries()));
        // Case-insensitive parameter lookup (handles contractID, contractId, CONTRACTID, etc.)
        let contractId = getSearchParam(searchParams, 'contractID');
        console.log('[MakePaymentLink] contractId from URL (raw):', contractId);

        // Trim whitespace and trailing slashes
        if (contractId) {
            contractId = String(contractId).trim().replace(/\/+$/, '');
            console.log('[MakePaymentLink] contractId from URL (trimmed):', contractId);
        }

        console.log('[MakePaymentLink] isLoggedIn:', isLoggedIn);

        if (!contractId) {
            contractId = getPendingContractId();
            console.log('[MakePaymentLink] contractId from localStorage:', contractId);
            if (!contractId) {
                console.log('[MakePaymentLink] No contractId found, redirecting to Home');
                navigate(NavigationConfig.Home, { replace: true });
                return;
            }
        }

        if (!isLoggedIn) {
            // Store contractID in localStorage for validation after login
            console.log('[MakePaymentLink] Storing contractId in localStorage and redirecting to login');
            storePendingContractId(String(contractId));
            // Redirect to login (usePendingLinkRedirect hook will handle redirect after login)
            navigate(NavigationConfig.Login);
            return;
        }

        // User is logged in, fetch contracts and validate the contractID
        const fetchAndOpenMakePayment = async () => {
            try {
                dispatch(showLoader());
                const response = await fetchContracts(false);

                if (response.statusCode === 200 && response.data && response.data.length > 0) {
                    // Find contract matching the contractID (match against AspireId, the user-facing ID)
                    const matchingContract = response.data.find(
                        (contract: ContractApiResponse) => String(contract.AspireId) === String(contractId),
                    );

                    if (matchingContract) {
                        // Clear localStorage and navigate to DashboardMakePayment with selected contract
                        clearPendingContractId();
                        navigate(NavigationConfig.DashboardMakePayment, {
                            replace: true,
                            state: {
                                selectedContractIds: [String(matchingContract.AspireId)],
                                fromMakePaymentLink: true,
                            },
                        });
                    } else {
                        // Clear localStorage to prevent redirect loop
                        clearPendingContractId();
                        dispatch(
                            openToast({
                                show: true,
                                isAlertOpen: true,
                                message: 'Contract not found',
                                severity: 'error',
                            }),
                        );
                        navigate(NavigationConfig.ContractOverview, { replace: true });
                    }
                } else {
                    // Clear localStorage to prevent redirect loop
                    clearPendingContractId();
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: response.message || 'Failed to load contracts',
                            severity: 'error',
                        }),
                    );
                    navigate(NavigationConfig.ContractOverview, { replace: true });
                }
            } catch (err) {
                // Clear localStorage to prevent redirect loop
                clearPendingContractId();
                console.error('Error fetching contracts for MakePaymentLink:', err);
                const e = err as { message?: string; response?: { data?: { message?: string } } };
                const errorMessage =
                    e?.response?.data?.message || e?.message || 'Failed to load contracts. Please try again.';
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: errorMessage,
                        severity: 'error',
                    }),
                );
                navigate(NavigationConfig.ContractOverview, { replace: true });
            } finally {
                dispatch(hideLoader());
            }
        };

        fetchAndOpenMakePayment();
    }, [searchParams, navigate, isLoggedIn, dispatch, fetchContracts]);

    // Show loader while processing
    return <Loader />;
};
export { MakePaymentLink, MAKEPAYMENT_CONTRACTID_STORAGE_KEY };
